import { CSSProperties, FC, JSX, useEffect, useRef, useState } from 'react'
import css from 'classnames'
import { Props, TooltipPosition } from './types'
import { HelpSVG } from '../SVG/HelpSVG'
import { SVGComment } from '../SVG/SVGComment'

export const ToolTip: FC<Props> = ({
  tooltipText,
  tooltipTitle = null,
  clicked,
  comment = false,
  children,
  wrapperClassName,
  minWidth = '240px',
  maxWidth = '360px',
  placement = 'bottom',
}): JSX.Element => {
  const [tooltipStatus, setTooltipStatus] = useState(false)
  const [tooltipPosition, setTooltipPosition] = useState<TooltipPosition>({
    top: 0,
    left: 0,
  })
  const tooltipRef = useRef<HTMLDivElement | null>(null)
  const tooltipChildRef = useRef<HTMLDivElement | null>(null)

  const handleMouseEnter = () => {
    if (!clicked) {
      setTooltipStatus(true)
    }
  }

  const handleMouseLeave = () => {
    if (!clicked) {
      setTooltipStatus(false)
    }
  }

  const handleClick = () => {
    if (clicked) {
      setTooltipStatus(!tooltipStatus)
    }
  }

  useEffect(() => {
    if (tooltipRef.current && tooltipChildRef.current) {
      const iconRect = tooltipRef.current.getBoundingClientRect()
      const { innerWidth, innerHeight } = window
      const tooltipWidth = tooltipChildRef.current.offsetWidth
      const tooltipHeight = tooltipChildRef.current.offsetHeight

      let top =
        placement === 'right'
          ? iconRect.top + iconRect.height / 2 - tooltipHeight / 2
          : iconRect.bottom
      let left = placement === 'right' ? iconRect.right + 12 : iconRect.left

      if (left + tooltipWidth > innerWidth) {
        left = Math.max(8, innerWidth - tooltipWidth - 8)
      }

      if (top + tooltipHeight > innerHeight) {
        top = Math.max(8, innerHeight - tooltipHeight - 8)
      }

      if (top < 8) {
        top = 8
      }

      setTooltipPosition({ top, left })
    }
  }, [placement, tooltipStatus])

  const tooltipStyle: CSSProperties = {
    position: 'fixed',
    top: `${tooltipPosition.top}px`,
    left: `${tooltipPosition.left}px`,
    zIndex: 99999,
    maxWidth,
    minWidth,
    backgroundColor: 'white',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.2)',
    borderRadius: '8px',
    padding: '12px',
  }

  return (
    <div
      className={wrapperClassName || 'flex items-center justify-center'}
      ref={tooltipRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}
    >
      <div className="relative">
        {children ||
          (comment ? (
            <div className="mr-2 cursor-pointer flex items-center justify-center">
              <SVGComment
                size="24px"
                color="#242424"
                className="absolute top-[6.5px] right-[-9px] "
              />
            </div>
          ) : (
            <div className="mr-2 cursor-pointer flex items-center justify-center">
              <HelpSVG />
            </div>
          ))}
        {tooltipStatus && (
          <div style={tooltipStyle} ref={tooltipChildRef}>
            {tooltipTitle ? (
              <h1 className="text-sm font-medium text-[#242424]">
                {tooltipTitle}
              </h1>
            ) : null}

            <div
              className={css(
                'w-full text-xs font-normal leading-4 text-[#707070]',
                {
                  'mt-2': tooltipTitle,
                },
              )}
            >
              {tooltipText}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
